import {ThreeAnnotationBase} from "@app/template-editor/helpers/three-annotation-base"
import {anyDifference, arrayDifferent} from "@template-editor/helpers/change-detection"
import {ThreeObject, mathIsEqual, setThreeObjectPart} from "@template-editor/helpers/three-object"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"

import {Three as THREE} from "@cm/material-nodes/three"
import {SceneNodePart, DimensionGuideInfo, SceneNodes, DimensionGuideEntry} from "@cm/template-nodes"
import {Vector3} from "@cm/math"
import {AnnotationStyleRegistry} from "@app/template-editor/helpers/annotation-style-registry"
import {Subscription} from "rxjs"

const css = `
.cm-dimension-guide-label {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #00000080;
    box-shadow: 0 0 0 2px #ffffff80;
    font-size: 14px;
    font-weight: bold;
    color: white;
    border-radius: 8px;
    padding: 3px;
}
.cm-dimension-guide-label-container {
    position: absolute;
    user-select: none;
    pointer-events: auto;
}
.cm-annotation-container.editor {
    cursor: pointer;
}
.cm-dimension-guide-label-container.editor:hover .cm-dimension-guide-label {
    color: #40c4ff;
    box-shadow: 0 0 0 2px #40c4ff;
}`

AnnotationStyleRegistry.getInstance().registerStyles("dimension-guide-styles", css)

class ThreeDimensionGuideLabel extends ThreeAnnotationBase {
    private readonly labelContainerElement: HTMLDivElement
    private readonly labelElement: HTMLDivElement
    private readonly descriptionElem: HTMLDivElement
    private concealable: boolean = false

    constructor(threeSceneManagerService: ThreeSceneManagerService, sceneNodePart: SceneNodePart) {
        super(threeSceneManagerService, sceneNodePart)

        this.labelElement = document.createElement("div")
        this.labelElement.className = "cm-dimension-guide-label"
        this.descriptionElem = document.createElement("div")

        this.labelContainerElement = document.createElement("div")
        this.labelContainerElement.className = "cm-dimension-guide-label-container"
        this.labelContainerElement.appendChild(this.labelElement)

        if (threeSceneManagerService.$displayMode() === "editor") this.labelContainerElement.classList.add("editor")

        this.init()
    }

    protected updateStyle(highlighted: boolean, selected: boolean): void {
        const backgroundColor = highlighted ? "red" : ""
        const pointerEvents = selected ? "none" : "auto"

        if (this.labelElement.style.backgroundColor !== backgroundColor || this.labelContainerElement.style.pointerEvents !== pointerEvents) {
            this.labelElement.style.backgroundColor = backgroundColor
            this.labelContainerElement.style.pointerEvents = pointerEvents
            this.dispose(false)
            this.threeSceneManagerService.redraw()
        }
    }

    setConcealable(concealable: boolean) {
        this.concealable = concealable
    }

    isConcealable() {
        return this.concealable
    }

    updateLabels(label: string, description: string) {
        // Use textContent so that user-controlled label/description values from template scene
        // graph data cannot inject HTML (these guides bypass Angular's sanitizer).
        this.labelElement.textContent = label
        this.descriptionElem.textContent = description
        this.dispose(false)
    }

    protected getContainer(): HTMLDivElement {
        return this.labelContainerElement
    }

    getTranslationOffset(element: HTMLDivElement) {
        return {x: 10, y: -element.clientHeight / 2}
    }

    getOcclusionOpacity(): number {
        return 0.2
    }
}

class ThreeDirectionIndicator extends THREE.Group {
    private lineGeometry: THREE.BufferGeometry
    private lineMaterial: THREE.LineBasicMaterial
    private line: THREE.LineSegments
    private startArrow: THREE.Mesh
    private endArrow: THREE.Mesh
    private arrowGeometry: THREE.CylinderGeometry
    private arrowMaterial: THREE.MeshBasicMaterial
    private arrowLength: number

    constructor(color: number, arrowLength: number, arrowWidth: number) {
        super()
        this.arrowLength = arrowLength

        this.lineGeometry = new THREE.BufferGeometry()
        this.lineGeometry.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array(6), 3))
        this.lineMaterial = new THREE.LineBasicMaterial({color})

        this.line = new THREE.LineSegments(this.lineGeometry, this.lineMaterial)
        this.line.raycast = () => null
        this.line.renderOrder = 1
        this.add(this.line)

        this.arrowGeometry = new THREE.CylinderGeometry(0, arrowWidth, this.arrowLength, 5)
        this.arrowMaterial = new THREE.MeshBasicMaterial({color})

        this.startArrow = new THREE.Mesh(this.arrowGeometry, this.arrowMaterial)
        this.endArrow = new THREE.Mesh(this.arrowGeometry.clone(), this.arrowMaterial)
        this.startArrow.renderOrder = 1
        this.endArrow.renderOrder = 1

        this.add(this.startArrow)
        this.add(this.endArrow)
    }

    setPoints(start: Vector3, end: Vector3) {
        const direction = end.sub(start).normalized()

        const adjustedStart = start.add(direction.mul(this.arrowLength / 2))
        const adjustedEnd = end.sub(direction.mul(this.arrowLength / 2))

        const positionAttr = this.lineGeometry.getAttribute("position")
        if (!(positionAttr instanceof THREE.BufferAttribute)) throw new Error("Expected position attribute to be a BufferAttribute")
        positionAttr.set([...adjustedStart.toArray(), ...adjustedEnd.toArray()])
        positionAttr.needsUpdate = true

        this.startArrow.position.set(adjustedStart.x, adjustedStart.y, adjustedStart.z)
        this.endArrow.position.set(adjustedEnd.x, adjustedEnd.y, adjustedEnd.z)

        const threeDirection = new THREE.Vector3(direction.x, direction.y, direction.z)
        this.startArrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), threeDirection)
        this.endArrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), threeDirection)
    }

    setConcealable(concealable: boolean) {
        this.lineMaterial.depthTest = concealable
        this.lineMaterial.depthWrite = concealable
        this.lineMaterial.needsUpdate = true

        this.arrowMaterial.depthTest = concealable
        this.arrowMaterial.depthWrite = concealable
        this.arrowMaterial.needsUpdate = true
    }

    dispose() {
        this.lineGeometry.dispose()
        this.lineMaterial.dispose()
        this.arrowGeometry.dispose()
        this.arrowMaterial.dispose()
    }
}

class DimensionGuideComponents extends THREE.Object3D {
    protected label: ThreeDimensionGuideLabel | undefined
    protected directionIndicator: ThreeDirectionIndicator

    constructor(
        private threeSceneManagerService: ThreeSceneManagerService,
        lineColor: number,
        arrowLength: number,
        arrowWidth: number,
        sceneNode: SceneNodes.SceneNode,
    ) {
        super()
        this.directionIndicator = new ThreeDirectionIndicator(lineColor, arrowLength, arrowWidth)
        this.add(this.directionIndicator)
        this.setupLabel(sceneNode)
    }

    private setupLabel(sceneNode: SceneNodes.SceneNode) {
        if (this.label) return

        this.label = new ThreeDimensionGuideLabel(this.threeSceneManagerService, {sceneNode, part: "root"})
        this.add(this.label)
    }

    public update(guide: DimensionGuideEntry, concealable: boolean) {
        const start = guide.start
        const end = guide.end

        if (!this.label) throw new Error("Label not initialized")

        this.label.position.copy(start.add(end.sub(start).div(2)))
        this.label.updateLabels(guide.label, "Dimension Guide")
        this.label.setConcealable(concealable)
        this.directionIndicator.setPoints(start, end)
        this.directionIndicator.setConcealable(concealable)
    }

    public dispose() {
        this.label?.dispose(true)
        this.directionIndicator.dispose()
    }
}

const getDimensionGuideEntries = (guides: DimensionGuideInfo[]): DimensionGuideEntry[] => {
    return guides.flatMap((guide) => (Array.isArray(guide) ? guide : [guide]))
}

export class ThreeDimensionGuides extends ThreeObject<SceneNodes.DimensionGuides> {
    protected override renderObject = new THREE.Group()

    private components: DimensionGuideComponents[] = []
    private lineColor = 0x353d37
    private arrowLength = 2.5
    private arrowWidth = 0.6

    private subscription: Subscription | undefined

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        setThreeObjectPart(this.getRenderObject(), this)

        this.subscription = this.threeSceneManagerService.preRender$.subscribe(({camera, taaFrame}) => {
            if (taaFrame !== null || !this.parameters) return

            let curIdx = 0
            this.parameters.guides.forEach((guide) => {
                if (!Array.isArray(guide)) {
                    this.components[curIdx].visible = true
                    curIdx++
                } else {
                    let closestIndex = -1
                    let closestDistance = Infinity
                    guide.forEach((guide) => {
                        this.components[curIdx].visible = false

                        const center = guide.start.add(guide.end).div(2)
                        const distanceToCamera = camera.position.distanceToSquared(new THREE.Vector3(center.x, center.y, center.z))
                        if (distanceToCamera < closestDistance) {
                            closestIndex = curIdx
                            closestDistance = distanceToCamera
                        }

                        curIdx++
                    })

                    if (closestIndex !== -1) this.components[closestIndex].visible = true
                }
            })
        })
    }

    private getOrCreateComponent(index: number, sceneNode: SceneNodes.SceneNode): DimensionGuideComponents {
        if (this.components[index]) return this.components[index]

        const component = new DimensionGuideComponents(this.threeSceneManagerService, this.lineColor, this.arrowLength, this.arrowWidth, sceneNode)
        this.components[index] = component
        return component
    }

    private setupComponents(dimensionGuideEntries: DimensionGuideEntry[], sceneNode: SceneNodes.SceneNode) {
        while (this.components.length > dimensionGuideEntries.length) {
            const component = this.components.pop()
            if (component) {
                this.renderObject.remove(component)
                component.dispose()
            }
        }

        dimensionGuideEntries.forEach((_, index) => {
            const component = this.getOrCreateComponent(index, sceneNode)
            this.renderObject.add(component)
        })
    }

    private updateComponents(dimensionGuideEntries: DimensionGuideEntry[], concealable: boolean) {
        dimensionGuideEntries.forEach((guide, index) => {
            if (guide && this.components[index]) this.components[index].update(guide, concealable)
        })
    }

    override setup(sceneNode: SceneNodes.DimensionGuides) {
        const dimensionGuideEntries = getDimensionGuideEntries(sceneNode.guides)
        return anyDifference([
            arrayDifferent(
                dimensionGuideEntries,
                this.parameters ? getDimensionGuideEntries(this.parameters.guides) : undefined,
                (valueA, valueB) => {
                    return (
                        valueA == valueB && mathIsEqual(valueA?.start, valueB?.end) && mathIsEqual(valueA?.end, valueB?.end) && valueA?.label === valueB?.label
                    )
                },
                () => {
                    this.setupComponents(dimensionGuideEntries, sceneNode)
                    this.updateComponents(dimensionGuideEntries, sceneNode.concealable)
                },
            ),
        ])
    }

    override dispose(final: boolean) {
        this.components.forEach((component) => component.dispose())
        this.components = []

        if (final) this.subscription?.unsubscribe()
    }
}
